import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo } from "react"
import { createPortal } from "react-dom"
import classnames from "classnames"

import styles from "./Modal.module.scss"

const shouldUseDialogElement = typeof HTMLDialogElement === "function"

type ModalProps = {
  className?: string
  style?: any
  children?: React.ReactNode
  onOpen?: any
  onClose?: any
  isDialog?: boolean
}

const Modal = ({ className, style, children, onOpen, onClose, isDialog = true }: ModalProps, ref: any) => {
  const modal: any = useMemo(
    () => document.createElement(shouldUseDialogElement && isDialog ? "dialog" : "div"),
    [isDialog],
  )

  const openModal = useCallback(
    (payload: any) => {
      const container: any = document.getElementById("modal")
      container.appendChild(modal)
      if (shouldUseDialogElement && isDialog) {
        modal?.showModal()
      } else {
        document.body.classList.add("overlay")
      }
      if (typeof onOpen === "function") {
        onOpen(payload)
      }
    },
    [modal, onOpen, isDialog],
  )

  const closeModal = useCallback(() => {
    if (typeof onClose === "function" && onClose() === false) {
      return // when onClose return false, cancel the close action
    }
    const container: any = document.getElementById("modal")
    if (container.contains(modal)) {
      if (shouldUseDialogElement && isDialog) {
        modal.close()
      }
      container.removeChild(modal)
      if (!container.childElementCount) {
        document.body.classList.remove("overlay")
      }
    }
  }, [modal, onClose, isDialog])

  useEffect(() => closeModal, [closeModal]) //change onClose will close modal

  useLayoutEffect(() => {
    modal.className = classnames(styles.container, className)
    if (style) {
      for (const property of Object.keys(style)) {
        modal.style.setProperty(property, style[property])
      }
    }
  }, [modal, className, style])

  useImperativeHandle(ref, () => ({
    modal,
    open: openModal,
    close: closeModal,
  }))

  return createPortal(children, modal)
}

export default forwardRef(Modal)
